/**
 * ExecutiveReportPage — V2 dashboard-style executive report.
 *
 * Fixed timeframe (Yesterday). Displays 5 sections: excess alarms table,
 * reason chart, care setting averages, response distribution charts,
 * and top 10 locations. Uses AlertDetails ReportFilterDrawer for filters.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession, getCampusId, getUserId, getSessionId, getCampusList,
    updateSessionCampus, getUsername,
} from '../../../services/session.js';
import { iAmHigherAuthority, formatDateIso, formatDateTime12Hour, getToday, getWeekRange, getMonthRange, getQuarterRange } from '../../../libs.jsx';
import {
    fetchExecutiveReport, fetchExecutiveFilterOptions, saveExecThreshold,
} from './api/executiveApi.js';
import { addEventNote, editEventReason, fetchPreformNotes } from '../alert-details/api/alertDetailsApi.js';

import ReportFilterDrawer from '../alert-details/components/ReportFilterDrawer.jsx';
import ScheduleReportDrawer from '../alert-details/components/ScheduleReportDrawer.jsx';
import { fetchFrequencies, createSchedule } from '../scheduled/api/scheduledApi.js';
import EventNoteDialog from '../alert-details/components/EventNoteDialog.jsx';
import EditReasonDialog from '../alert-details/components/EditReasonDialog.jsx';
import ExcessAlarmsTable from './components/ExcessAlarmsTable.jsx';
import ReasonChart from './components/ReasonChart.jsx';
import CareSettingTable from './components/CareSettingTable.jsx';
import ResponseDistribution from './components/ResponseDistribution.jsx';
import TopLocationsTable from './components/TopLocationsTable.jsx';

import './executive-report-page.css';

const TIME_RANGES = [
    { id: 1, label: 'Today', getRange: () => { const d = getToday(); d.setHours(0,0,0,0); const e = new Date(); return [d, e]; } },
    { id: 2, label: 'Yesterday', getRange: () => { const d = getToday(-1); d.setHours(0,0,0,0); const e = getToday(-1); e.setHours(23,59,59,999); return [d, e]; } },
    { id: 3, label: 'This Week', getRange: () => { const [s, e] = getWeekRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 4, label: 'This Month', getRange: () => { const [s] = getMonthRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 5, label: 'This Quarter', getRange: () => { const [s] = getQuarterRange(0); s.setHours(0,0,0,0); const now = new Date(); return [s, now]; } },
    { id: 6, label: 'Last Week', getRange: () => { const [s, e] = getWeekRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 7, label: 'Last Month', getRange: () => { const [s, e] = getMonthRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
    { id: 8, label: 'Last Quarter', getRange: () => { const [s, e] = getQuarterRange(-1); s.setHours(0,0,0,0); e.setHours(23,59,59,999); return [s, e]; } },
];

function emptyReportFilters() {
    return {
        areas: [], areaGroups: [], areaTypes: [],
        residents: [], residentGroups: [],
        users: [], userGroups: [],
        deviceCategories: [], devices: [],
        eventTypes: [], reasons: [],
        responseTime: { enabled: false, operator: 'lt', value: 60 },
    };
}

const ExecutiveReportPage = () => {
    // -- Session --
    const session = useMemo(() => getSession(), []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => {
        if (!session?.campusData || !campusId) return null;
        return session.campusData[campusId.toString()] ?? null;
    }, [session, campusId]);
    const tzOffset = campusConfig?.tz_offset;

    // -- Report state --
    const [status, setStatus] = useState('init');
    const [excessiveAlerts, setExcessiveAlerts] = useState([]);
    const [reasonData, setReasonData] = useState({});
    const [areaTypesResponse, setAreaTypesResponse] = useState([]);
    const [areaTypesChart, setAreaTypesChart] = useState({});
    const [dailyAreas, setDailyAreas] = useState([]);

    // -- Session feature flags --
    const eventNotesActive = Boolean(session?.event_notes_active);
    const eventNotesPreform = Boolean(session?.event_notes_preform);
    const eventNotesFreeform = Boolean(session?.event_notes_freeform);
    const isReasonEditable = Boolean(session?.is_reason_editable);

    // -- Note / Reason dialog state --
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteTarget, setNoteTarget] = useState(null);
    const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
    const [reasonTarget, setReasonTarget] = useState(null);
    const [prenoteData, setPrenoteData] = useState([]);
    const [saving, setSaving] = useState(false);

    // -- Exec threshold --
    const defaultExecTime = campusConfig?.exec_excess_time || 20;
    const [execTime, setExecTime] = useState(defaultExecTime);
    const [savingThreshold, setSavingThreshold] = useState(false);

    // -- Filters --
    const [reportFilters, setReportFilters] = useState(emptyReportFilters);
    const [filterOptions, setFilterOptions] = useState({});
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // -- Time range --
    const [rangeId, setRangeId] = useState(2);
    const [rangeName, setRangeName] = useState('Yesterday');
    const [customRange, setCustomRange] = useState(false);
    const [start, setStart] = useState(() => { const d = getToday(-1); d.setHours(0,0,0,0); return d; });
    const [end, setEnd] = useState(() => { const d = getToday(-1); d.setHours(23,59,59,999); return d; });

    // -- Schedule drawer --
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [frequencies, setFrequencies] = useState([]);

    // -- Time range popover --
    const [timePopoverOpen, setTimePopoverOpen] = useState(false);
    const timePopoverRef = useRef(null);
    const pendingAutoRunRef = useRef(false);

    // -- Toast --
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const shouldRerunRef = useRef(false);
    const autoRunRef = useRef(true);

    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // -- Load filter options + preform notes on mount / campus change --
    useEffect(() => {
        if (!campusId) return;
        fetchExecutiveFilterOptions(campusId).then(setFilterOptions);
        fetchFrequencies().then((d) => setFrequencies(Array.isArray(d) ? d : []));
        if (eventNotesActive) {
            fetchPreformNotes(campusId).then(setPrenoteData);
        }
    }, [campusId, eventNotesActive]);

    // -- Auto-run on first load --
    useEffect(() => {
        if (autoRunRef.current) {
            autoRunRef.current = false;
            handleRunReport();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // -- Time range popover — outside click + escape --
    useEffect(() => {
        if (!timePopoverOpen) return;
        const handleClick = (e) => {
            if (timePopoverRef.current && !timePopoverRef.current.contains(e.target)) {
                setTimePopoverOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setTimePopoverOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [timePopoverOpen]);

    // -- Time range helpers --
    const handleTimeRange = useCallback((range) => {
        const [s, e] = range.getRange();
        setStart(s);
        setEnd(e);
        setRangeId(range.id);
        setRangeName(range.label);
        setCustomRange(false);
    }, []);

    const handleCustomToggle = useCallback(() => {
        setCustomRange(true);
        setRangeId(0);
        setRangeName('Custom');
    }, []);

    /** Open Custom in the Report Settings drawer from the popover */
    const handlePopoverCustom = useCallback(() => {
        setTimePopoverOpen(false);
        handleCustomToggle();
        setFilterDrawerOpen(true);
    }, [handleCustomToggle]);

    // -- Run Report --
    const handleRunReport = useCallback(async () => {
        setStatus('loading');

        const now = new Date();
        const endToUse = end > now ? now : end;
        const stD = formatDateIso(start);
        const enD = formatDateIso(endToUse);

        const filterPayload = {
            areas: (reportFilters.areas || []).join(','),
            areaGroups: (reportFilters.areaGroups || []).join(','),
            areaTypes: (reportFilters.areaTypes || []).join(','),
            residents: (reportFilters.residents || []).join(','),
            residentGroups: (reportFilters.residentGroups || []).join(','),
            users: (reportFilters.users || []).join(','),
            userGroups: (reportFilters.userGroups || []).join(','),
            deviceCategories: (reportFilters.deviceCategories || []).join(','),
            devices: (reportFilters.devices || []).join(','),
            eventTypes: (reportFilters.eventTypes || []).join(','),
            reasons: (reportFilters.reasons || []).join(','),
        };

        const result = await fetchExecutiveReport({
            campusId,
            username: getUsername(),
            startDate: stD,
            endDate: enD,
            filters: filterPayload,
            execTime,
        });

        if (result.ErrorCode) {
            const errMap = {
                11: 'Report timed out. Contact your customer success manager for large reports.',
                10: 'An unknown error occurred. Please contact customer success if the problem persists.',
                12: 'Request unauthorized. Service may be unavailable.',
            };
            showToast('error', errMap[result.ErrorCode] || 'Report request failed.');
            setStatus('init');
            return;
        }

        setExcessiveAlerts(result.excessiveAlerts || []);
        setReasonData(result.reasonData || {});
        setAreaTypesResponse(result.areaTypesResponse || []);
        setAreaTypesChart(result.areaTypesResponseChart?.chart || {});
        setDailyAreas(result.dailyAreas || []);
        setStatus('ready');
    }, [campusId, start, end, reportFilters, execTime, showToast]);

    // -- Re-run after filter apply --
    useEffect(() => {
        if (shouldRerunRef.current) {
            shouldRerunRef.current = false;
            handleRunReport();
        }
    }, [reportFilters, handleRunReport]);

    // -- Auto-run report after quick-select time range change --
    useEffect(() => {
        if (pendingAutoRunRef.current) {
            pendingAutoRunRef.current = false;
            handleRunReport();
        }
    });

    /** Quick-select a preset from the time range popover — applies + auto-runs */
    const handleQuickTimeRange = useCallback((range) => {
        handleTimeRange(range);
        pendingAutoRunRef.current = true;
        setTimePopoverOpen(false);
    }, [handleTimeRange]);

    // -- Campus change --
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        setCampusId(val);
        updateSessionCampus(val);
        setStatus('init');
        setExcessiveAlerts([]);
        setReasonData({});
        setAreaTypesResponse([]);
        setAreaTypesChart({});
        setDailyAreas([]);
        const s = getSession();
        const newCfg = s?.campusData?.[val.toString()];
        setExecTime(newCfg?.exec_excess_time || 20);
    }, []);

    // -- Save threshold --
    const handleSaveThreshold = useCallback(async () => {
        setSavingThreshold(true);
        const result = await saveExecThreshold(campusId, execTime, defaultExecTime, getUserId(), getSessionId());
        setSavingThreshold(false);
        if (result[0]) {
            showToast('success', `Threshold saved to ${execTime} minutes`);
            // Update session
            const s = getSession();
            if (s?.campusData?.[campusId.toString()]) {
                s.campusData[campusId.toString()].exec_excess_time = execTime;
            }
        } else {
            showToast('error', result[1] || 'Failed to save threshold');
        }
    }, [campusId, execTime, defaultExecTime, showToast]);

    // -- Filter handlers --
    const handleApplyFilters = useCallback((filters) => {
        setReportFilters(filters);
        setFilterDrawerOpen(false);
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    const handleClearReportFilters = useCallback(() => {
        setReportFilters(emptyReportFilters());
        if (status === 'ready') shouldRerunRef.current = true;
    }, [status]);

    // Filter chips
    const reportFilterChips = useMemo(() => {
        const chips = [];
        const labelMap = {
            areas: 'Areas', areaGroups: 'Area Groups', areaTypes: 'Area Types',
            residents: 'Residents', residentGroups: 'Resident Groups',
            users: 'Users', userGroups: 'User Groups',
            deviceCategories: 'Device Categories', devices: 'Devices',
            eventTypes: 'Event Types', reasons: 'Reasons',
        };
        Object.entries(reportFilters).forEach(([key, vals]) => {
            if (key === 'responseTime') {
                if (vals?.enabled) chips.push({ key, label: `Response Time: ${vals.operator} ${vals.value}min` });
            } else if (Array.isArray(vals) && vals.length > 0) {
                chips.push({ key, label: `${labelMap[key] || key}: ${vals.length}` });
            }
        });
        return chips;
    }, [reportFilters]);

    const activeFilterCount = reportFilterChips.length;

    // -- Note dialog handlers --
    const handleOpenNoteDialog = useCallback((event) => {
        setNoteTarget(event);
        setNoteDialogOpen(true);
    }, []);

    const handleSaveNote = useCallback(async (eventId, noteText) => {
        setSaving(true);
        const result = await addEventNote(eventId, campusId, noteText, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Note added successfully.');
            setNoteDialogOpen(false);
            setNoteTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to add note.');
        }
    }, [campusId, showToast, handleRunReport]);

    // -- Reason dialog handlers --
    const handleOpenReasonDialog = useCallback((event) => {
        setReasonTarget(event);
        setReasonDialogOpen(true);
    }, []);

    const handleSaveReason = useCallback(async (eventId, newReasonId, oldReasonId) => {
        setSaving(true);
        const result = await editEventReason(eventId, newReasonId, oldReasonId, campusId, getUserId(), getSessionId());
        setSaving(false);
        if (result[0]) {
            showToast('success', 'Reason updated successfully.');
            setReasonDialogOpen(false);
            setReasonTarget(null);
            handleRunReport();
        } else {
            showToast('error', result[1] || 'Failed to update reason.');
        }
    }, [campusId, showToast, handleRunReport]);

    // Schedule
    const handleScheduleSave = useCallback(async (payload) => {
        const result = await createSchedule(payload, { campusId, userId: getUserId(), clientId: getSessionId() });
        if (result[0]) { showToast('success', 'Schedule created successfully.'); setScheduleDrawerOpen(false); }
        else { showToast('error', result[1] || 'Failed to create schedule.'); }
    }, [campusId, showToast]);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ar-page">
                    {/* Toast */}
                    {toast ? (
                        <div className={`ap-toast ${toast.type === 'success' ? 'ap-toast--success' : 'ap-toast--error'}`} role="status" aria-live="polite">
                            {toast.type === 'success' ? (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            ) : (
                                <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                </svg>
                            )}
                            <span>{toast.message}</span>
                            <button type="button" onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }} className="ap-toast__close" aria-label="Dismiss">
                                <svg className="ap-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ) : null}

                    {/* PAGE HEADER */}
                    <div className="ar-page-header">
                        <div className="ar-page-header__inner">
                            <div className="ar-breadcrumb-row">
                                <div className="ar-breadcrumb">
                                    <span className="ar-breadcrumb__text">Reports</span>
                                    <svg className="ar-icon-md ar-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                    <span className="ar-breadcrumb__current">Executive</span>
                                </div>
                                <div className="ar-campus-row">
                                    <label htmlFor="ex-campus-selector" className="ar-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select id="ex-campus-selector" value={campusId} onChange={handleCampusChange} className="ar-campus-select">
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ar-campus-name">{campusConfig?.name || ''}</span>
                                    )}
                                </div>
                            </div>

                            <div className="ar-title-row">
                                <div className="ar-title-row__left">
                                    <h1 className="ar-page-title">Executive Report</h1>
                                    <div className="ar-time-badge-wrap" ref={timePopoverRef}>
                                        <span
                                            className={`ar-time-summary${timePopoverOpen ? ' ar-time-summary--open' : ''}`}
                                            onClick={() => setTimePopoverOpen((p) => !p)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTimePopoverOpen((p) => !p); } }}
                                            aria-expanded={timePopoverOpen}
                                            aria-haspopup="true"
                                        >
                                            <i className="fe fe-clock" style={{ marginRight: '4px', fontSize: 'var(--font-size-xs)' }} />
                                            {customRange
                                                ? `${formatDateTime12Hour(start)} – ${formatDateTime12Hour(end)}`
                                                : rangeName}
                                            <svg className="ar-time-summary__chevron" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                        </span>

                                        {timePopoverOpen ? (
                                            <div className="ar-time-popover">
                                                <div className="ar-time-popover__grid">
                                                    {TIME_RANGES.map((range) => (
                                                        <button
                                                            key={range.id}
                                                            type="button"
                                                            className={`ar-time-popover__btn${rangeId === range.id && !customRange ? ' ar-time-popover__btn--active' : ''}`}
                                                            onClick={() => handleQuickTimeRange(range)}
                                                        >
                                                            {range.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="ar-time-popover__divider" />
                                                <button
                                                    type="button"
                                                    className={`ar-time-popover__custom${customRange ? ' ar-time-popover__custom--active' : ''}`}
                                                    onClick={handlePopoverCustom}
                                                >
                                                    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                                    </svg>
                                                    Custom Range…
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="ar-title-row__actions">
                                    <button className="dp-btn dp-btn--secondary dp-btn--sm" onClick={() => setScheduleDrawerOpen(true)}>
                                        <i className="fe fe-calendar" style={{ marginRight: '4px' }} />Schedule Report
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--secondary dp-btn--sm"
                                        onClick={() => setFilterDrawerOpen(true)}
                                        style={{ position: 'relative' }}
                                    >
                                        <i className="fe fe-sliders" style={{ marginRight: '4px' }} />
                                        Report Settings
                                        {activeFilterCount > 0 ? (
                                            <span className="ar-filter-badge">{activeFilterCount}</span>
                                        ) : null}
                                    </button>
                                    <button
                                        className="dp-btn dp-btn--primary dp-btn--sm"
                                        onClick={handleRunReport}
                                        disabled={status === 'loading'}
                                    >
                                        <i className="fe fe-refresh-cw" style={{ marginRight: '4px' }} />
                                        {status === 'loading' ? 'Running...' : 'Run Report'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div className="ar-content">
                        {/* Filter chips */}
                        {reportFilterChips.length > 0 ? (
                            <div className="ar-filter-chips">
                                {reportFilterChips.map((chip) => (
                                    <span key={chip.key} className="ar-filter-chip">{chip.label}</span>
                                ))}
                                <button className="ar-filter-chips__clear" onClick={handleClearReportFilters}>Clear All</button>
                            </div>
                        ) : null}

                        {/* Init */}
                        {status === 'init' ? (
                            <div className="ar-init-message">
                                <i className="fe fe-bar-chart-2" style={{ fontSize: '48px', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }} />
                                <p>Click <strong>Run Report</strong> to generate yesterday's executive summary.</p>
                            </div>
                        ) : null}

                        {/* Loading */}
                        {status === 'loading' ? (
                            <div className="ar-loading"><div className="ar-loading__spinner" /><p>Loading executive report...</p></div>
                        ) : null}

                        {/* Report sections */}
                        {status === 'ready' ? (
                            <div className="ex-sections">
                                <ExcessAlarmsTable
                                    data={excessiveAlerts}
                                    execTime={execTime}
                                    onExecTimeChange={setExecTime}
                                    onSaveThreshold={handleSaveThreshold}
                                    savingThreshold={savingThreshold}
                                    tzOffset={tzOffset}
                                    onAddNote={handleOpenNoteDialog}
                                    onEditReason={handleOpenReasonDialog}
                                    eventNotesActive={eventNotesActive}
                                    isReasonEditable={isReasonEditable}
                                />
                                <ReasonChart reasonData={reasonData} />
                                <CareSettingTable data={areaTypesResponse} />
                                <ResponseDistribution chartData={areaTypesChart} />
                                <TopLocationsTable data={dailyAreas} />
                            </div>
                        ) : null}
                    </div>

                    {/* Filter Drawer — reuse AlertDetails drawer, hide time range */}
                    <ReportFilterDrawer
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        onApply={handleApplyFilters}
                        onClear={handleClearReportFilters}
                        filterOptions={filterOptions}
                        currentFilters={reportFilters}
                        saving={false}
                        timeRanges={TIME_RANGES}
                        rangeId={rangeId}
                        customRange={customRange}
                        start={start}
                        end={end}
                        onTimeRangeChange={handleTimeRange}
                        onCustomToggle={handleCustomToggle}
                        onStartChange={setStart}
                        onEndChange={setEnd}
                    />

                    {/* Schedule Report Drawer */}
                    <ScheduleReportDrawer
                        open={scheduleDrawerOpen}
                        onClose={() => setScheduleDrawerOpen(false)}
                        onSave={handleScheduleSave}
                        saving={false}
                        reportTypeId={5}
                        rangeId={rangeId}
                        rangeName={rangeName}
                        reportFilters={reportFilters}
                        filterOptions={filterOptions}
                        campusId={campusId}
                        frequencies={frequencies}
                        tzOffset={tzOffset}
                    />

                    {/* Event Note Dialog */}
                    {noteDialogOpen && noteTarget ? (
                        <EventNoteDialog
                            event={noteTarget}
                            prenoteData={prenoteData}
                            eventNotesPreform={eventNotesPreform}
                            eventNotesFreeform={eventNotesFreeform}
                            tzOffset={tzOffset}
                            onClose={() => { setNoteDialogOpen(false); setNoteTarget(null); }}
                            onSave={handleSaveNote}
                            saving={saving}
                        />
                    ) : null}

                    {/* Edit Reason Dialog */}
                    {reasonDialogOpen && reasonTarget ? (
                        <EditReasonDialog
                            event={reasonTarget}
                            reasonCodes={filterOptions.reasonCodes || []}
                            onClose={() => { setReasonDialogOpen(false); setReasonTarget(null); }}
                            onSave={handleSaveReason}
                            saving={saving}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default ExecutiveReportPage;
